import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'
import routes from './routes'
import { can } from '@/utils/permission.helper'
import { SESSION } from '@/constants'
import { getProfile } from '@/helpers/services/profile.service'

// Refresh cached permissions once per app load. The web app resumes with a
// stored token and doesn't always re-hit login, so a role change (e.g. newly
// granted Create/Update Client) can leave localStorage permissions stale.
let _permissionsRefreshed = false
async function refreshPermissionsOnce() {
  if (_permissionsRefreshed) return
  _permissionsRefreshed = true
  try {
    const res = await getProfile()
    const perms = res && res.data && res.data.permissions
    if (Array.isArray(perms)) {
      localStorage.setItem(SESSION.PERMISSIONS, JSON.stringify(perms))
    }
  } catch (e) {
    // Keep cached permissions on failure (offline, etc.).
  }
}

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  const publicPages = ['/login', '/reset-password', '/staff-reset-password', '/first-time-password', '/staff-first-time-password', '/forgot-password', '/staff-login', '/staff-forgot-password', '/jobs'];

  // Check if the route is a public page or matches the dynamic public-report path
  const isPublicReport = routeTo.path.startsWith('/public-report/');
  const authpage = !publicPages.includes(routeTo.path) && !isPublicReport;

  const loggeduser = localStorage.getItem('user');
  const loggedStaff = localStorage.getItem('staffUser');

  // Redirect to login if trying to access a protected page without being logged in
  if (authpage && !loggeduser && !loggedStaff) {
    return next('/login');
  }

  // Pull the fresh permission set once so a role change applies without a
  // full logout/login (runs before the RBAC check below).
  if (authpage && (loggeduser || loggedStaff)) {
    await refreshPermissionsOnce();
  }

  // RBAC: block admin routes the user lacks permission for. Staff routes carry
  // no permission meta, so this only affects the admin app.
  if (authpage && loggeduser) {
    const required = routeTo.meta && routeTo.meta.permission;
    if (required && !can(required)) {
      return next('/');
    }
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

export default router
